import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@react-md/button';
import { TextField } from '@react-md/form';
import { FeatureContent } from '@components/feature-content';
import { FormField, NonFieldErrors } from '@components/form-field';
import { Icons } from '@components/icons';
import { SubmitButton } from '@components/submit-button';
import type { FetchBaseQueryError } from '@services/df/baseApi';
import { useUserFormLabels } from '@modules/user';
import { setApiErrors } from '@utils';
import { useValidation } from '@hooks';
import { createLoginValidation } from '../form-validation';
import { ResetPasswordButton } from './reset-password-button';
import { SignInWithSso } from './sign-in-with-sso';
import { signInId } from '../helpers';
import { useSignInFormFlow } from '../hooks';
import { SignInForm } from '../types';
import { SignInFormId } from '../constants';

interface UseSignInFormProps {
    email?: string;
    request: (data: SignInForm) => Promise<{ isSignInSuccess: boolean; error?: FetchBaseQueryError }>;
    onSuccess?: () => void;
}

const useSignInForm = ({ email, request, onSuccess }: UseSignInFormProps) => {
    const validationSchema = useValidation(createLoginValidation);
    const formApi = useForm<SignInForm>({
        defaultValues: {
            email: email ?? '',
            password: '',
        },
        resolver: yupResolver(validationSchema),
    });

    const {
        setError,
        formState: { isSubmitting: isTokenObtaining },
    } = formApi;

    const submit: SubmitHandler<SignInForm> = async form => {
        const { isSignInSuccess, error } = await request(form);

        if (isSignInSuccess) {
            onSuccess?.();
        } else if (error) {
            const errors = error.data;
            setApiErrors(setError, errors);
        }
    };
    return {
        formApi,
        isTokenObtaining,
        submit,
    };
};

interface Props extends UseSignInFormProps {
    slug: string;
    email?: string;
    resetSignInOptions?: () => void;
    isSsoEnabled: boolean;
    hasSsoOnly?: boolean;
}

export const AuthSignInForm: React.FC<Props> = ({
    isSsoEnabled,
    hasSsoOnly,
    email: _email,
    resetSignInOptions,
    slug,
    request,
    onSuccess,
    children,
}) => {
    const hasDefinedEmail = !!_email;
    const { isBaseForm, showBaseForm } = useSignInFormFlow({ isSsoEnabled });
    const { formApi, isTokenObtaining, submit } = useSignInForm({ email: _email, request, onSuccess });

    const {
        control,
        handleSubmit,
        watch,
        trigger,
        formState: { errors },
    } = formApi;

    const { t } = useTranslation();
    const labels = useUserFormLabels(t);

    const email = watch('email');

    return (
        <>
            <form
                id={SignInFormId}
                className={cn('fieldset-grid-cell', 'fieldset-grid')}
                onSubmit={handleSubmit(submit)}
            >
                <FormField
                    prefix={SignInFormId}
                    name="email"
                    control={control}
                    component={TextField}
                    label={labels.email}
                    widgetProps={
                        hasDefinedEmail
                            ? {
                                  disabled: true,
                                  isRightAddon: false,
                                  rightChildren: (
                                      <Button
                                          id={signInId('edit_email')}
                                          buttonType="icon"
                                          theme="primary"
                                          onClick={resetSignInOptions}
                                      >
                                          <Icons.Edit />
                                      </Button>
                                  ),
                              }
                            : undefined
                    }
                />

                {isBaseForm && (
                    <FormField
                        prefix={SignInFormId}
                        name="password"
                        control={control}
                        component={TextField}
                        label={labels.password}
                        widgetProps={{
                            type: 'password',
                            className: 'rmd-text-field-container--autogrow-addon',
                            isRightAddon: false,
                            rightChildren: <ResetPasswordButton email={email} trigger={trigger} />,
                        }}
                    />
                )}

                <NonFieldErrors errors={errors} />
            </form>

            <div className={cn('emulated-flex-gap')}>
                {isBaseForm ? (
                    <SubmitButton prefix={signInId()} form={SignInFormId} isSending={isTokenObtaining}>
                        <FeatureContent contentKey={signInId('submit')} fallback={t('buttons.signIn', 'Sign in')} />
                    </SubmitButton>
                ) : hasSsoOnly ? null : (
                    <FeatureContent
                        contentKey={signInId('show_base_form')}
                        fallback={t('buttons.enterPassword', 'Enter password')}
                        wrapper={props => (
                            <Button
                                className="rmd-button--text-large"
                                theme="primary"
                                themeType="contained"
                                onClick={showBaseForm}
                                {...props}
                            />
                        )}
                    />
                )}

                {isSsoEnabled && <SignInWithSso slug={slug} email={email} trigger={trigger} />}
            </div>
        </>
    );
};
